import type { JWTInput } from 'google-auth-library';

import type { vendorApiNames } from '../names';
import type {
  BaseUpdateVendorApiSettings,
  BaseVendorApiWithMultipleRateLimiters,
  RateLimiters,
} from './base';
import type { VendorApiHttpAdvancedSettings } from './http';

// rate limiter ids to follow metric names as described by https://cloud.google.com/compute/api-quota
export const gcpRateLimiterIds = [
  'compute.googleapis.com/default',
  'compute.googleapis.com/read_requests',
  'compute.googleapis.com/list_requests',
  'compute.googleapis.com/operation_read_requests',
  'compute.googleapis.com/heavy_weight_read_requests',
  'cloudasset.googleapis.com/search_requests',
] as const;

export type GcpRateLimiterId = (typeof gcpRateLimiterIds)[number];

export const regionalGcpRateLimiterIds = [
  'compute.googleapis.com/default_per_region',
  'compute.googleapis.com/read_requests_per_region',
  'compute.googleapis.com/list_requests_per_region',
  'compute.googleapis.com/operation_read_requests_per_region',
  'compute.googleapis.com/get_serial_port_output_requests_per_region',
  'compute.googleapis.com/heavy_weight_read_requests_per_region',
  'compute.googleapis.com/instance_list_referrers_requests_per_region',
  'compute.googleapis.com/network_endpoint_list_requests_per_region',
  'compute.googleapis.com/regional_network_endpoint_list_requests_per_region',
] as const;

export type RegionalGcpRateLimiterId =
  (typeof regionalGcpRateLimiterIds)[number];

export interface GcpAuthentication {
  credentialsJson: JWTInput & Record<string, unknown>;
}

/**
 * GCP API settings available from API
 */
export type PublicGcpApiSettings = BaseVendorApiWithMultipleRateLimiters<
  (typeof vendorApiNames)['gcp'],
  GcpRateLimiterId
> &
  VendorApiHttpAdvancedSettings & {
    scopes?: string[];
    regionalRateLimiters?: Partial<RateLimiters<RegionalGcpRateLimiterId>>;
  };

/**
 * GCP API settings available in backend
 */
export type GcpApiSettings = PublicGcpApiSettings & GcpAuthentication;

/**
 * GCP API settings expected on API when updating
 */
export type UpdateGcpApiSettings = BaseUpdateVendorApiSettings<
  PublicGcpApiSettings,
  GcpAuthentication
>;
